/**
 * screens/AnalyticsScreen.tsx
 * Outcomes & Analytics — 3-col KPI grid + outcome-distribution bars.
 * UI recreated verbatim from the design HTML (lines ~387-411), max-width 1000.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, ActivityIndicator } from 'react-native';
import { useTheme } from '../context/themeContext';
import { Card, ScreenBody } from '../components/voiceUi';
import { ANALYTICS_KPIS, ANALYTICS_DIST } from '../data/voiceMock';
import { getAnalytics } from '../services/voice/voiceService';

// Outcome-distribution bar color by label (matches the Call Log dots). Any other
// label (e.g. a sentiment breakdown) stays neutral ink.
function distColor(label: string, c: any): string {
  const l = label.toLowerCase();
  if (l.includes('complet')) return c.outcomeOk;
  if (l.includes('miss')) return c.outcomeMiss;
  if (l.includes('drop') || l.includes('abandon')) return c.outcomeDrop;
  return c.ink;
}

export default function AnalyticsScreen() {
  const { theme } = useTheme();
  const c = theme.colors;

  const [kpis, setKpis] = useState<typeof ANALYTICS_KPIS>([]);
  const [dist, setDist] = useState<typeof ANALYTICS_DIST>([]);
  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, live: isLive } = await getAnalytics();
    setKpis(data.kpis);
    setDist(data.dist);
    setLive(isLive);
    setLoading(false);
  }, []);
  // Plain effect — the console shell has no React Navigation (no useFocusEffect).
  useEffect(() => { void load(); }, [load]);

  if (loading) {
    return (
      <ScreenBody maxWidth={1000}>
        <View style={{ padding: 40, alignItems: 'center' }}><ActivityIndicator color={c.ink3} /></View>
      </ScreenBody>
    );
  }

  return (
    <ScreenBody maxWidth={1000}>
      {!live && (
        <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginBottom: 8 }}>
          <Text style={{ fontSize: 11.5, color: c.ink4, fontFamily: theme.font.mono }}>Couldn’t load — check connection</Text>
        </View>
      )}

      {/* 3-col KPI grid (2 rows of 3) */}
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        {kpis.map(k => (
          <View key={k.label} style={{ flexBasis: '31%', flexGrow: 1 }}>
            <Card style={{ padding: 16, paddingVertical: 15 }}>
              <Text style={{ fontSize: 11, color: c.ink3, fontWeight: '500', marginBottom: 8, fontFamily: theme.font.sans }}>{k.label}</Text>
              <Text style={{ fontSize: 28, fontWeight: '600', letterSpacing: -0.56, lineHeight: 28, color: c.ink, fontFamily: theme.font.sans }}>{k.value}</Text>
              <Text style={{ fontSize: 11, color: c.ink4, marginTop: 6, fontFamily: theme.font.sans }}>{k.sub}</Text>
            </Card>
          </View>
        ))}
      </View>

      {/* Outcome distribution */}
      <Card style={{ padding: 18 }}>
        <Text style={{ fontWeight: '600', fontSize: 13.5, marginBottom: 16, color: c.ink, fontFamily: theme.font.sans }}>Outcome distribution</Text>
        <View style={{ gap: 13 }}>
          {dist.map(d => (
            <View key={d.label} style={{ flexDirection: 'row', alignItems: 'center', gap: 14 }}>
              <Text style={{ width: 150, fontSize: 12.5, color: c.ink2, fontFamily: theme.font.sans }}>{d.label}</Text>
              <View style={{ flex: 1, height: 10, backgroundColor: c.paper3, borderRadius: 20, overflow: 'hidden' }}>
                <View style={{ height: '100%', width: `${d.pct}%`, backgroundColor: distColor(d.label, c), borderRadius: 20 }} />
              </View>
              <Text style={{ width: 70, textAlign: 'right', fontFamily: theme.font.mono, fontSize: 12, color: c.ink3 }}>{d.n} · {d.pct}%</Text>
            </View>
          ))}
        </View>
      </Card>
    </ScreenBody>
  );
}
